import { type CharacterData, type CharacterCollection, tryParseCharacterCollection, tryParseCharacterData } from "~/types/storage";
import { createDefaultCharacterCollectionData, createDefaultCharacterData } from "~/utils/characterDataFactory";

export const COLLECTION_STORAGE_KEY = "divide_ttrpg_characters";

/**
 * Load character collection from localStorage with migrations.
 */
export function loadCharacterCollection(): CharacterCollection {
  try {
    const stored = localStorage.getItem(COLLECTION_STORAGE_KEY);
    const data = JSON.parse(stored || 'null')
    return tryParseCharacterCollection(data) || createDefaultCharacterCollectionData();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Error loading character collection:", err);
    return createDefaultCharacterCollectionData();
  }
}

/**
 * Save character collection to localStorage.
 */
export function saveCharacterCollection(collection: CharacterCollection): void {
  try {
    localStorage.setItem(COLLECTION_STORAGE_KEY, JSON.stringify(collection));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Error saving character collection:", err);
  }
}

// ============================================================================
// Collection Helpers
// ============================================================================

export function getActiveCharacter(collection: CharacterCollection): CharacterData {
  const active = collection.characters.find(c => c.id === collection.activeCharacterId);
  return active ?? collection.characters[0] ?? createDefaultCharacterData();
}

export function updateCharacterInCollection(
  collection: CharacterCollection,
  updatedCharacter: CharacterData
): CharacterCollection {
  return {
    ...collection,
    characters: collection.characters.map(c =>
      c.id === updatedCharacter.id ? updatedCharacter : c
    ),
  };
}

export function addCharacterToCollection(
  collection: CharacterCollection,
  character: CharacterData
): CharacterCollection {
  return {
    ...collection,
    characters: [...collection.characters, character],
  };
}

export function removeCharacterFromCollection(
  collection: CharacterCollection,
  characterId: string
): CharacterCollection {
  const filteredCharacters = collection.characters.filter(c => c.id !== characterId);

  if (filteredCharacters.length === 0) {
    return createDefaultCharacterCollectionData();
  }

  let activeCharacterId = collection.activeCharacterId;
  if (activeCharacterId === characterId) {
    activeCharacterId = filteredCharacters[0].id;
  }

  return {
    ...collection,
    activeCharacterId,
    characters: filteredCharacters,
  };
}

export function setActiveCharacterInCollection(
  collection: CharacterCollection,
  characterId: string
): CharacterCollection {
  if (!collection.characters.find(c => c.id === characterId)) {
    return collection;
  }

  return {
    ...collection,
    activeCharacterId: characterId,
  };
}

// ============================================================================
// Import/Export
// ============================================================================

export function exportCharacterData(data: CharacterData, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename || "character"}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function importCharacterData(file: File): Promise<CharacterData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        const data = tryParseCharacterData(parsed);
        if (!data) {
          reject(new Error("Invalid character data"));
        } else {
          resolve(data);
        }
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsText(file);
  });
}
